import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  TextInput,
  Select,
  Switch,
  Button,
  Group,
  Stack,
  Grid,
  Paper,
  Text,
} from "@mantine/core";

function AddRepModal({ opened, onClose, onSuccess }) {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [repTypes, setRepTypes] = useState([]);
  const [recruitingSources, setRecruitingSources] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    isManager: false,
    recruitedBy: "",
    manager: "",
    repRank: "",
    recruitingSource: "",
  });

  useEffect(() => {
    if (opened) {
      loadDropdownData();
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        isManager: false,
        recruitedBy: "",
        manager: "",
        repRank: "",
        recruitingSource: "",
      });
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Set default rep rank once types load
  useEffect(() => {
    if (repTypes.length > 0 && !formData.repRank) {
      setFormData(prev => ({ ...prev, repRank: repTypes[0].id.toString() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repTypes]);

  const loadDropdownData = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };

      const [repsRes, typesRes, sourcesRes] = await Promise.all([
        fetch(getApiEndpoint("/v1/rep/reps-list"), { headers }),
        fetch(getApiEndpoint("/v1/company/rep-types"), { headers }),
        fetch(getApiEndpoint("/v1/company/recruiting-sources"), { headers }),
      ]);

      if (repsRes.ok) {
        const d = await repsRes.json();
        setUsers(d.data?.users || []);
        setManagers(d.data?.managers || []);
      }
      if (typesRes.ok) {
        const d = await typesRes.json();
        setRepTypes(d.data || []);
      }
      if (sourcesRes.ok) {
        const d = await sourcesRes.json();
        setRecruitingSources(d.data || []);
      }
    } catch {
      // silently ignore — dropdowns are best-effort
    }
  };

  const set = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      return "First and last name are required.";
    }
    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      return "A valid email address is required.";
    }
    if (!formData.repRank) {
      return "Rep Rank is required.";
    }
    const isCompanyAdmin = user?.role === "COMPANY_ADMIN";
    if (!isCompanyAdmin && (!formData.recruitedBy || !formData.manager)) {
      return "Recruited By and Manager are required.";
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/rep/add-rep"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          is_manager: formData.isManager,
          recruited_by: formData.recruitedBy
            ? parseInt(formData.recruitedBy)
            : null,
          manager: formData.manager ? parseInt(formData.manager) : null,
          rep_type_id: formData.repRank ? parseInt(formData.repRank) : null,
          recruiting_source_id: formData.recruitingSource
            ? parseInt(formData.recruitingSource)
            : null,
          commission_scale: [],
          has_override: false,
          override_scale: [],
          has_marketing_deal: false,
          marketing_deal_scale: [],
          seasons: [],
        }),
      });

      if (response.ok) {
        showSuccess("Rep added successfully!");
        onSuccess?.();
        onClose();
      } else {
        let msg = "Failed to create rep";
        try {
          const data = await response.json();
          if (data.message) msg = data.message;
          else if (data.error) msg = data.error;
          if (
            msg.toLowerCase().includes("email") &&
            (msg.toLowerCase().includes("exists") ||
              msg.toLowerCase().includes("duplicate") ||
              msg.toLowerCase().includes("already"))
          ) {
            msg = "A rep with that email already exists.";
          }
        } catch {
          // use default
        }
        setError(msg);
      }
    } catch (e) {
      setError(e.message || "Failed to save rep");
    } finally {
      setLoading(false);
    }
  };

  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add New Rep"
      centered
      size="lg"
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={e => set("firstName", e.currentTarget.value)}
              required
              disabled={loading}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={e => set("lastName", e.currentTarget.value)}
              required
              disabled={loading}
              size="md"
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="email"
              label="Email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={e => set("email", e.currentTarget.value)}
              required
              disabled={loading}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Paper
              p="sm"
              radius="sm"
              style={{
                backgroundColor: "#fff",
                border: "1px solid #ced4da",
                height: "42px",
                display: "flex",
                alignItems: "center",
                marginTop: "25px",
              }}
            >
              <Group justify="space-between" align="center" w="100%">
                <Text size="sm" fw={500} c="#1f2937">
                  Has Manager Privileges?
                </Text>
                <Switch
                  checked={formData.isManager}
                  onChange={e => set("isManager", e.currentTarget.checked)}
                  disabled={loading}
                  size="lg"
                  onLabel="YES"
                  offLabel="NO"
                  styles={{ track: { width: "70px" } }}
                />
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Recruited By"
              placeholder="Select recruiter..."
              data={users.map(u => ({
                value: u.rep_id.toString(),
                label: `${u.first_name} ${u.last_name}`,
              }))}
              value={formData.recruitedBy}
              onChange={v => set("recruitedBy", v)}
              required={!isCompanyAdmin}
              disabled={loading}
              searchable
              clearable
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Manager"
              placeholder="Select manager..."
              data={managers.map(m => ({
                value: m.rep_id.toString(),
                label: `${m.first_name} ${m.last_name}`,
              }))}
              value={formData.manager}
              onChange={v => set("manager", v)}
              required={!isCompanyAdmin}
              disabled={loading}
              searchable
              clearable
              size="md"
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Rep Rank"
              placeholder="Select rep rank..."
              data={repTypes.map(t => ({
                value: t.id.toString(),
                label: t.name,
              }))}
              value={formData.repRank}
              onChange={v => set("repRank", v)}
              required
              disabled={loading}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Recruiting Source"
              placeholder="Select recruiting source..."
              data={recruitingSources.map(s => ({
                value: s.id.toString(),
                label: s.source_name,
              }))}
              value={formData.recruitingSource}
              onChange={v => set("recruitingSource", v)}
              disabled={loading}
              clearable
              size="md"
            />
          </Grid.Col>
        </Grid>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading} disabled={loading}>
            Save Rep
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddRepModal;
